import { put, takeLatest } from 'redux-saga/effects'
import { notification } from 'antd'
import i18next from 'i18next'
import {
  getTaskCategories,
  getTaskUserList,
  registerNewTask,
  getTasks,
  updateTask,
  deleteTask,
  deleteSelectedTasks,
  updateTaskOrder,
  updateSelectedTasks,
  getUnreadTasks,
  updateTaskReadStatus
} from '../../../apis'
import {
  loadTaskCategoriesSuccess,
  loadTaskCategoriesError,
  loadTasksSuccess,
  loadTasksError,
  loadTaskUserListSuccess,
  loadTaskUserListError,
  updateTaskSuccess,
  updateTaskError,
  deleteTaskSuccess,
  deleteTaskError,
  deleteSelectedTasksSuccess,
  deleteSelectedTasksError,
  loadMoreTasksSuccess,
  loadMoreTasksError,
  loadUnreadTasks,
  loadUnreadTasksSuccess,
  loadUnreadTasksError
} from './actions'
import {
  LOAD_TASK_CATEGORIES,
  LOAD_TASK_USER_LIST,
  REGISTER_NEW_TASK,
  LOAD_TASKS,
  UPDATE_TASK,
  DELETE_TASK,
  DELETE_SELECTED_TASKS,
  UPDATE_TASK_ORDER,
  UPDATE_SELECTED_TASKS,
  LOAD_MORE_TASKS,
  LOAD_UNREAD_TASKS
} from './constants'
import { REQUEST } from '../../../store'

function* loadTaskCategoriesSaga() {
  try {
    const { data } = yield getTaskCategories()
    yield put(loadTaskCategoriesSuccess(data))
  } catch (error) {
    yield put(loadTaskCategoriesError(error))
    notification.error({
      message: i18next.t('common.error'),
      description: i18next.t('task.errors.load_task_category_error')
    })
  }
}

function* loadTaskUserListSaga({ payload }) {
  try {
    const { data } = yield getTaskUserList(payload)
    yield put(loadTaskUserListSuccess(data))
  } catch (error) {
    yield put(loadTaskUserListError(error))
    notification.error({
      message: i18next.t('common.error'),
      description: i18next.t('task.errors.load_user_list_error')
    })
  }
}

function* registerNewTaskSaga({ payload }) {
  try {
    const { callback } = payload
    if (callback) delete payload.callback

    yield registerNewTask(payload)
    notification.success({
      message: i18next.t('common.success'),
      description: i18next.t('task.success.register_task_success')
    })

    if (callback) callback()
  } catch (error) {
    notification.error({
      message: i18next.t('common.error'),
      description: i18next.t('task.errors.register_task_error')
    })
  }
}

function* loadTasksSaga({ payload }) {
  try {
    const { data } = yield getTasks(payload)
    yield put(loadTasksSuccess(data))

    const taskIds = data.items.filter(({ isRead }) => !isRead).map(({ id }) => id)
    if (taskIds.length > 0) {
      yield updateTaskReadStatus(taskIds)
      yield put(loadUnreadTasks())
    }
  } catch (error) {
    yield put(loadTasksError(error))
    notification.error({
      message: i18next.t('common.error'),
      description: i18next.t('task.errors.load_tasks_error')
    })
  }
}

function* updateTaskSaga({ payload }) {
  try {
    const { callback } = payload
    if (callback) delete payload.callback

    yield updateTask(payload)
    yield put(updateTaskSuccess(payload))
    notification.success({
      message: i18next.t('common.success'),
      description: i18next.t('task.success.update_task_success')
    })

    if (callback) callback()
  } catch (error) {
    yield put(updateTaskError(error))
    notification.error({
      message: i18next.t('common.error'),
      description: i18next.t('task.errors.update_task_error')
    })
  }
}

function* deleteTaskSaga({ payload }) {
  try {
    const { id, callback } = payload
    if (callback) delete payload.callback

    yield deleteTask(id)
    yield put(deleteTaskSuccess())
    notification.success({
      message: i18next.t('common.success'),
      description: i18next.t('task.success.delete_task_success')
    })

    if (callback) callback()
  } catch (error) {
    yield put(deleteTaskError(error))
    notification.error({
      message: i18next.t('common.error'),
      description: i18next.t('task.errors.delete_task_error')
    })
  }
}

function* deleteSelectedTasksSaga({ payload }) {
  try {
    const { taskIds, callback } = payload
    yield deleteSelectedTasks(taskIds)
    yield put(deleteSelectedTasksSuccess())
    if (callback) callback()
    notification.success({
      message: i18next.t('common.success'),
      description: i18next.t('task.success.delete_task_success')
    })
  } catch (error) {
    yield put(deleteSelectedTasksError(error))
    notification.error({
      message: i18next.t('common.error'),
      description: i18next.t('task.errors.delete_task_error')
    })
  }
}

function* updateTaskOrderSaga({ payload }) {
  try {
    yield updateTaskOrder(payload)
  } catch (error) {
    notification.error({
      message: i18next.t('common.error'),
      description: i18next.t('task.errors.update_task_order_error')
    })
  }
}

function* updateSelectedTasksSaga({ payload }) {
  try {
    const { taskIds, status, dueDate, callback } = payload
    yield updateSelectedTasks({ taskIds, status, dueDate })
    notification.success({
      message: i18next.t('common.success'),
      description: i18next.t('task.success.update_task_success')
    })
    if (callback) callback()
  } catch (error) {
    notification.error({
      message: i18next.t('common.error'),
      description: i18next.t('task.errors.update_task_error')
    })
  }
}

function* loadMoreTasksSaga({ payload }) {
  try {
    const { data } = yield getTasks(payload)
    yield put(loadMoreTasksSuccess(data))

    const taskIds = data.items.filter(({ isRead }) => !isRead).map(({ id }) => id)
    if (taskIds.length > 0) {
      yield updateTaskReadStatus(taskIds)
      yield put(loadUnreadTasks())
    }
  } catch (error) {
    yield put(loadMoreTasksError(error))
    notification.error({
      message: i18next.t('common.error'),
      description: i18next.t('task.errors.load_tasks_error')
    })
  }
}

function* loadUnreadTasksSaga() {
  try {
    const { data } = yield getUnreadTasks()
    yield put(loadUnreadTasksSuccess(data))
  } catch (error) {
    yield put(loadUnreadTasksError(error))
    notification.error({
      message: i18next.t('common.error'),
      description: i18next.t('task.errors.load_unread_tasks_error')
    })
  }
}

export default function* taskManagementSaga() {
  yield takeLatest(REQUEST(LOAD_TASK_CATEGORIES), loadTaskCategoriesSaga)
  yield takeLatest(REQUEST(LOAD_TASK_USER_LIST), loadTaskUserListSaga)
  yield takeLatest(REQUEST(REGISTER_NEW_TASK), registerNewTaskSaga)
  yield takeLatest(REQUEST(LOAD_TASKS), loadTasksSaga)
  yield takeLatest(REQUEST(UPDATE_TASK), updateTaskSaga)
  yield takeLatest(REQUEST(DELETE_TASK), deleteTaskSaga)
  yield takeLatest(REQUEST(DELETE_SELECTED_TASKS), deleteSelectedTasksSaga)
  yield takeLatest(REQUEST(UPDATE_TASK_ORDER), updateTaskOrderSaga)
  yield takeLatest(REQUEST(UPDATE_SELECTED_TASKS), updateSelectedTasksSaga)
  yield takeLatest(REQUEST(LOAD_MORE_TASKS), loadMoreTasksSaga)
  yield takeLatest(REQUEST(LOAD_UNREAD_TASKS), loadUnreadTasksSaga)
}
